import React from "react";
import _ from "lodash";
import {Grid, makeStyles} from "@material-ui/core";
import {ArrowBack} from "@material-ui/icons";

import BaseDialog from "../../../../../../components/BaseDialog/BaseDialog";
import {useConfirmationModalContext} from "../../../../../../components/ConfirmationModalContextProvider";
import ModelPortfolioSelection
  from "../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/ModelPortfolioSelection";
import Rebalancing from "../../../TradeStep/components/Rebalancing";
import PrimaryButton from "../../../../../../components/Buttons/PrimaryButton";
import {transactionValueToQuantity} from "../../../../utils";
import {ACTIONS, STEPS} from "./constants";
import {getListWithToggledItem} from "../PortfolioTradeV2/utils";

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    paddingLeft: 0,
    paddingRight: 0,
  },
});


function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SELECT_MODEL_PORTFOLIO:
      return {
        ...state,
        step: STEPS.INSTRUMENTS,
        selectedItem: action.payload,
      };
    case ACTIONS.CONFIRM_MODEL_PORTFOLIO:
      return {
        ...state,
        step: STEPS.REVIEW,
        modelPortfolioData: {
          name: (action.payload || state.selectedItem).name,
          categoryName: 'Musterdepot',
          assets: state.activeAssets.map(a => ({...a, weight: +a.weight / 100})),
        },
        selectedAssets: [],
      };
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.SET_SELECTED_ASSETS:
      return {...state, selectedAssets: action.payload};
    case ACTIONS.SET_ACTIVE_ASSETS:
      return {...state, activeAssets: action.payload};
    case ACTIONS.TOGGLE_ASSET: {
      return {
        ...state,
        selectedAssets: getListWithToggledItem(state.selectedAssets, action.payload, (a) => a.isin),
      };
    }
    case ACTIONS.TOGGLE_ACTIVE_ASSET: {
      return {
        ...state,
        activeAssets: getListWithToggledItem(state.activeAssets, action.payload, (a) => a.isin),
      };
    }
    default:
      return state;
  }
}

const initialState = {
  step: STEPS.SELECT,
  selectedItem: null,
  modelPortfolioData: {},
  selectedAssets: [],
  activeAssets: [],
};


const RebalancingModal = ({
  open,
  onClose,
  portfolioInstruments,
  allTransactions,
  clearTransactions,
  setRebalancingRecommendations,
  portfolioData,
  portfolioMarketValue,
  dataService,
  instrumentsHandler,
  withSrri,
  clientSRRI,
}) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const classes = useStyles();
  const {confirm} = useConfirmationModalContext();
  const {step, selectedItem, modelPortfolioData, selectedAssets, activeAssets} = state;
  const isReview = step === STEPS.REVIEW;
  const isInstruments = step === STEPS.INSTRUMENTS;

  const reset = React.useCallback(() => {
    dispatch({type: ACTIONS.RESET});
  }, []);

  const handleClose = React.useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset])

  const submitRebalancing = React.useCallback(async () => {
    const buy = [];
    const sell = [];
    selectedAssets.forEach(asset => {
      if (asset.transactionType === 'Verkauf') {
        sell.push({...asset, newQty: transactionValueToQuantity(asset.newMarketValue, {data: asset}, false)});
      } else if (asset.transactionType === 'Kauf') {
        buy.push(asset);
      }
    });

    await setRebalancingRecommendations(buy, sell);
    handleClose();
  }, [selectedAssets, setRebalancingRecommendations, handleClose]);

  const handleSubmit = React.useCallback(async () => {
    const hasExisting = _.flatten(Object.values(allTransactions || {})).length > 0;
    if (hasExisting) {
      const confirmed = await confirm("Alle vorherigen Ordereingaben werden gelöscht. Sind Sie sicher?");
      if (!confirmed) return;
      clearTransactions();
    }
    submitRebalancing();
  }, [allTransactions, confirm, clearTransactions, submitRebalancing]);

  const confirmModelPortfolio = React.useCallback(() => {
    dispatch({type: ACTIONS.CONFIRM_MODEL_PORTFOLIO});
  }, []);

  const handleDetailsClick = React.useCallback((item, skipDetails) => {
    dispatch({type: skipDetails ? ACTIONS.CONFIRM_MODEL_PORTFOLIO : ACTIONS.SELECT_MODEL_PORTFOLIO, payload: item});
  }, []);

  const handleInstrumentSelect = React.useCallback((asset) => {
    dispatch({type: ACTIONS.TOGGLE_ACTIVE_ASSET, payload: asset});
  }, []);

  const handleSetActiveAssets = React.useCallback((assets) => {
    dispatch({type: ACTIONS.SET_ACTIVE_ASSETS, payload: assets});
  }, []);

  const handleReviewInstrumentSelect = React.useCallback((asset) => {
    dispatch({type: ACTIONS.TOGGLE_ASSET, payload: asset});
  }, []);

  const handleReviewSelectAll = React.useCallback((assets) => {
    dispatch({type: ACTIONS.SET_SELECTED_ASSETS, payload: assets});
  }, []);

  if (!open) return null;

  const actions = (isReview || isInstruments) ? (
    <Grid container spacing={2} justify="space-between">
      <Grid item>
        <PrimaryButton
          variant="outlined"
          icon={<ArrowBack color="primary" />}
          text="Schließen"
          onButtonClick={reset}
        />
      </Grid>
      <Grid item style={{marginLeft: 'auto'}}>
        {isInstruments ? (
          <PrimaryButton
            disabled={activeAssets.length === 0}
            text="Speichern & fortfahren"
            onButtonClick={confirmModelPortfolio}
          />
        ) : (
          <PrimaryButton
            disabled={selectedAssets.length === 0}
            icon={<i className="far fa-dollar-sign fa-xs" style={{color: 'white'}} />}
            text="ORDER"
            onButtonClick={handleSubmit}
          />
        )}
      </Grid>
    </Grid>
  ) : undefined;

  return (
    <>
      <BaseDialog
        open={true}
        maxWidth="lg"
        fullWidth
        disableBackdropClick
        handleClose={handleClose}
        title={isReview ? undefined : "Bitte wählen Sie ein Musterdepot für das Rebalancing aus"}
        actions={actions}
        customClasses={isReview ? undefined : {dialogContent: classes.dialogContent}}
        data-id="rebalancing-dialog"
      >
        {isReview ? (
          <Rebalancing
            portfolioInstruments={portfolioInstruments}
            instrumentsHandler={instrumentsHandler}
            selectedAssets={selectedAssets}
            onInstrumentSelect={handleReviewInstrumentSelect}
            onSelectAll={handleReviewSelectAll}
            portfolioData={portfolioData}
            modelPortfolioData={modelPortfolioData}
            withSrri={withSrri}
            clientSRRI={clientSRRI}
            totalMarketValue={portfolioMarketValue || 0}
          />
        ) : (
          <ModelPortfolioSelection
            onDetailsClick={handleDetailsClick}
            dataService={dataService}
            action="rebalancing"
            hideTitle
            depot={{}}
            isModelPortfolio={false}
            instrumentsHandler={instrumentsHandler}
            onInstrumentSelect={handleInstrumentSelect}
            onSelectAll={handleSetActiveAssets}
            selectedItem={selectedItem}
            selectedInstruments={activeAssets}
          />
        )}
      </BaseDialog>
    </>
  );
};

export default RebalancingModal;
